import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './auth.guard';
import { GIS_ROUTE_ROLES } from './gis-management/models/gis-roles';
import { DashboardComponent } from './dashboard/dashboard.component';
import { FieldComponent } from './field/field.component';
import { LandingComponent } from './public/landing/landing.component';
import { MappingComponent } from './mapping/mapping.component';
import { PriorityGroupsComponent } from './priority-groups/priority-groups.component';
import { ProfileComponent } from './profile/profile.component';
import { ActivationComponent } from './public/activation/activation.component';
import { RegistrationComponent } from './public/registration/registration.component'
import { VirtualConsultantComponent } from './virtual-consultant/virtual-consultant.component';
import {OrderComponent} from "./order-to-cash/components-otc/order-manager/order/order.component";
import {MyOrdersComponent} from "./order-to-cash/components-otc/order-manager/my-orders/my-orders.component";
import {OrderManagerComponent} from "./order-to-cash/components-otc/order-manager/order-manager/order-manager.component";
import {ApprovalsComponent} from "./order-to-cash/components-otc/order-manager/approvals/approvals.component";
import { PinManagerComponent } from "./pin-reworks/pin-manager/pin-manager.component";
import {AllPinsComponent} from "./pin-reworks/all-pins/all-pins.component";
import {SampleGroupsComponent} from "./pin-reworks/sample-groups/sample-groups.component";
import { AllSamplesComponent } from './pin-reworks/all-samples/all-samples.component';
import { FieldInspectionsTabComponent } from './pin-reworks/field-inspections-tab/field-inspections-tab.component';
import {LabManagerComponent} from "./lab-manager/lab-manager.component";
import {LabSubmissionsComponent} from "./lab-manager/lab-submissions/lab-submissions.component";
import {LabSubmissionDetailComponent} from "./lab-manager/lab-submission-detail/lab-submission-detail.component";
import {LabSamplesComponent} from "./lab-manager/lab-samples/lab-samples.component";
import {LabAnalysisRequiredComponent} from "./lab-manager/lab-analysis-required/lab-analysis-required.component";
import {LabCostCalculatorComponent} from "./lab-manager/lab-cost-calculator/lab-cost-calculator.component";
import { SeasonManagerComponent } from './season-manager/season-manager/season-manager.component';
import { SeasonComponent } from './season-manager/season/season.component';
import { SeasonBatchUpdateComponent } from './season-manager/season-batch-update/season-batch-update.component';
import { SeasonAdminComponent } from './season-manager/season-admin/season-admin.component';
import { SeasonFieldAdminSeasonsComponent } from './season-manager/season-field-admin-seasons/season-field-admin-seasons.component';
import { SeasonFieldAdminFieldsComponent } from './season-manager/season-field-admin-fields/season-field-admin-fields.component';
import { EditFieldLinkSeasonComponent } from './season-manager/edit-field-link-season/edit-field-link-season.component';
import { SeasonDataTableComponent } from './season-manager/season-data-table/season-data-table.component';
import { SeasonFieldOrderComponent } from './season-manager/season-field-order/season-field-order.component';
import { SeasonCostingComponent } from './season-manager/season-costing/season-costing.component';
import {LoginComponent} from "./public/login/login.component";
import {ForgotPasswordComponent} from "./public/forgot-password/forgot-password.component";
import {LabResultsComponent} from "./lab-manager/lab-results/lab-results.component";
import { TenantViewComponent } from './tenant-view/tenant-view.component';
import {RecommendationsComponent} from "./recommendation-manager/recommendations/recommendations.component";
import {
  RecommendationDetailsComponent
} from "./recommendation-manager/recommendation-details/recommendation-details.component";
import {
  CreateRecommendationComponent
} from "./recommendation-manager/create-recommendation/create-recommendation.component";
import { FieldDashboardViewComponent } from './field-dashboard-view/field-dashboard-view.component';
import { FieldAnalysisComponent } from './field-dashboard-view/field-analysis/field-analysis.component';
import { CostBreakdownComponent } from './field-dashboard-view/cost-breakdown/cost-breakdown.component';
import { ApproveOrderComponent } from './order-to-cash/components-otc/dialogs-otc/approve-order/approve-order.component';
import { ApprovedOrdersComponent } from './order-to-cash/components-otc/order-manager/approved-orders/approved-orders.component';
import { AnalysisSequenceComponent } from './sequencing/analysis-sequence/analysis-sequence.component';
import { CustomAnalysisSequenceComponent } from './sequencing/custom-analysis-sequence/custom-analysis-sequence.component';
import { ChemicalCompareComponent } from './chemical-compare/chemical-compare.component';
import { JdConnectComponent } from './john-deere/pages/connect/jd-connect.component';
import { JdCallbackComponent } from './john-deere/pages/callback/jd-callback.component';
import { JdConnectedComponent } from './john-deere/pages/connected/jd-connected.component';
import { FoConnectComponent } from './field-ops/pages/connect/fo-connect.component';
import { FoCallbackComponent } from './field-ops/pages/callback/fo-callback.component';
import { FoConnectedComponent } from './field-ops/pages/connected/fo-connected.component';
import { BatchCreateSamplesComponent } from './season-manager/batch-create-samples/batch-create-samples.component';

const routes: Routes = [ 
  {
    path: '',
    component: LandingComponent,
  },
  {
    path: 'order-manager/order/:id/redirect',
    component: LandingComponent,
  },
  {
    path: 'registration',
    component: RegistrationComponent,
  },
  {
    path: 'activation',
    component: ActivationComponent,
  },
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
  },
  {
    path: 'forgot-password/:email',
    component: ForgotPasswordComponent,
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularDashboard']
    }
  },
  {
    path: 'profile',
    component: ProfileComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularProfile']
    }
  },
  {
    path: 'tenant-view',
    component: TenantViewComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMapping']
    }
  },
  {
    path: 'mapping',
    component: MappingComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMapping']
    }
  },
  {
    path: 'priority-groups',
    component: PriorityGroupsComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularPriorityGroups']
    }
  },
  {
    path: 'field',
    component: FieldComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularField']
    },
    children: [
      {
        component: AnalysisSequenceComponent,
        path: 'sequence/:fieldId'
      },
      { path: 'custom-sequence/:fieldId',
        component: CustomAnalysisSequenceComponent 
      },
      { path: 'chemical-compare/:fieldId',
        component: ChemicalCompareComponent
      }
    ]
  },
  {
    path: 'field-dashboard-view/:id',
    component: FieldDashboardViewComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularField']
    },
    children: [
      {
        path: '',
        redirectTo: 'analysis',
        pathMatch: 'full'
      },
      {
        path: 'analysis',
        component: FieldAnalysisComponent
      },
      {
        path: 'cost-breakdown',
        component: CostBreakdownComponent
      },
      {
        component: AnalysisSequenceComponent,
        path: 'sequence/:fieldId'
      },
      { path: 'custom-sequence/:fieldId',
        component: CustomAnalysisSequenceComponent 
      },
      { path: 'chemical-compare/:fieldId',
        component: ChemicalCompareComponent
      }
    ]
  },
  {
    path: 'virtual-consultant',
    component: VirtualConsultantComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularVirtualConsultant']
    }
  },
  {
    path: 'lab-manager',
    redirectTo: 'lab-manager/submissions'
  },
  {
    path: 'lab-manager',
    component: LabManagerComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['LabManagerRepresentative', 'LabManagerAgent'],
    },
    children: [
      {
        component: LabSubmissionsComponent,
        path: 'submissions'
      },
      {
        component: LabSubmissionDetailComponent,
        path: 'submission/:id'
      },
      {
        component: LabSamplesComponent,
        path: 'samples/:id'
      },
      {
        component: LabAnalysisRequiredComponent,
        path: 'analysis-required/:id'
      },
      {
        component: LabCostCalculatorComponent,
        path: 'cost-calculator/:id'
      },
      {
        component: LabResultsComponent,
        path: 'results/:id'
      }
    ]
  },
  {
    path: 'season-manager',
    component: SeasonManagerComponent,
    canActivate: [AuthGuard],
    data: {
        shouldHaveRoles: ['AngularFieldAdmin']
    },
    children: [
      {
        path: `fields`,
        component: SeasonFieldAdminFieldsComponent,
      },
      {
        path: `seasons`,
        component: SeasonFieldAdminSeasonsComponent,
      },
    ],
  },

  {
    path: 'season/:id',
    component: SeasonComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularFieldAdmin']
    },
    children: [
      {
        path: `season-admin`,
        component: SeasonAdminComponent,
      },
      {
        path: `sample-batch-update`,
        component: SeasonBatchUpdateComponent,
      },
      {
        path: `costing`,
        component: SeasonCostingComponent,
      },
      {
        path: `data-table`,
        component: SeasonDataTableComponent,
      },
      {
        path: `field-order`,
        component: SeasonFieldOrderComponent,
      },
      {
        path: `batch-create-samples`,
        component: BatchCreateSamplesComponent,
      },
    ],
  },
  {
    path: 'order-manager',
    component: OrderManagerComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['OTCOverallApprovingManager', 'OTCApprovingManager', 'OTCAgent','OTCRepresentative'],
    },
   
    // canActivateChild: [AuthGuard],
    children: [
      {
        path: 'my-orders',
        component: MyOrdersComponent,
        data: {
          shouldHaveRoles: ['OTCOverallApprovingManager', 'OTCApprovingManager', 'OTCAgent', 'OTCRepresentative'],
          canActivate: [AuthGuard],
       },
      },
      {
        path: 'approvals',
        component: ApprovalsComponent,
        data: {
          shouldHaveRoles: ['OTCOverallApprovingManager', 'OTCApprovingManager', 'OTCAgent','OTCRepresentative'],
          canActivate: [AuthGuard],
       },
      },
      {
        path: 'approved-orders',
        component: ApprovedOrdersComponent,
        data: {
          shouldHaveRoles: ['OTCOverallApprovingManager', 'OTCApprovingManager', 'OTCAgent','OTCRepresentative'],
          canActivate: [AuthGuard],
       },
      },
      {
        path: 'new-order',
        component: OrderComponent,
        data: {
          shouldHaveRoles: ['OTCOverallApprovingManager', 'OTCApprovingManager', 'OTCAgent','OTCRepresentative'],
          canActivate: [AuthGuard],
       },
      },
      {
        path: `order/:id`,
        component: OrderComponent,
        data: {
          shouldHaveRoles: ['OTCOverallApprovingManager', 'OTCApprovingManager', 'OTCAgent','OTCRepresentative'],
           canActivate: [AuthGuard],
       },
      }
    ],
},
  // {
  //   path: 'order-tracking',
  //   component: OrderTrackingComponent,
  //   canActivate: [AuthGuard],
  //   data: {
  //     role: 'AngularProfile'
  //   }
  // },
  {
    path: 'pin-manager',
    component: PinManagerComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularPinManager']
    },
    children: [
      {
        path: `all-pins`,
        component: AllPinsComponent,
      },
      {
        path: `sample-groups`,
        component: SampleGroupsComponent,
      },
      {
        path: `field-inspections`,
        component: FieldInspectionsTabComponent,
        // Own guard: the parent's AngularPinManager check is short-circuited by the
        // allow-list in AuthGuard.checkAuthenticationDetails, so the tab would
        // otherwise be reachable by any logged-in user.
        canActivate: [AuthGuard],
        data: {
          shouldHaveRoles: ['InspectionRequestor', 'Inspector', 'InspectionViewer']
        }
      },
    ],
    
  },
  {
    path: 'recommendation-manager',
    redirectTo: 'recommendation-manager/recommendations'
  },
  {
    path: 'recommendation-manager',
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ["RecommendationManager"]
    },
    children: [
      {
        path: 'recommendations',
        component: RecommendationsComponent
      },
      {
        path: 'create-recommendation',
        component: CreateRecommendationComponent
      },
      {
        path: 'details/:id',
        component: RecommendationDetailsComponent
      }
    ]
  },

    {
      path: 'field-admin/edit-link-field/:id',
      component: EditFieldLinkSeasonComponent,
    },
  {
    path: `all-samples/:id`,
    component: AllSamplesComponent,
  },
  {
    path: 'john-deere/connect',
    component: JdConnectComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMapping']
    }
  },
  {
    path: 'john-deere/callback',
    component: JdCallbackComponent
  },
  {
    path: 'john-deere/connected',
    component: JdConnectedComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMapping']
    }
  },
  {
    path: 'field-ops/connect',
    component: FoConnectComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMapping']
    }
  },
  {
    path: 'field-ops/callback',
    component: FoCallbackComponent
  },
  {
    path: 'field-ops/connected',
    component: FoConnectedComponent,
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMapping']
    }
  },
  /**
   * Knowledge Base. YA-217.
   *
   * Guarded like every other route in this file: AuthGuard, with the three Knowledge Base roles
   * listed. Keegan's call on 21/09/2026, for consistency.
   *
   * **The list must never be empty.** In AuthGuard `hasAccess` starts false and only a MATCH sets
   * it true, so `shouldHaveRoles: []` refuses everybody and redirects to `mapping`. That was the
   * first version of this route, and the symptom was the nav link appearing to do nothing.
   *
   * **What it costs, written down so nobody rediscovers it as a bug.** AuthGuard matches against
   * the roles in sessionStorage, written once at sign-in, so a Knowledge Base role granted during
   * an open session does not work until the person signs out and back in. REQ-012 asks for it to
   * take effect immediately, and KnowledgeBaseAccessGuard inside the module does re-fetch, but it
   * never gets the chance because AuthGuard has already turned that person away.
   *
   * The inner guard still earns its place. It catches a role REVOKED mid-session, where the cache
   * still says yes and the fresh answer says no, and it sends refusals to a page that explains
   * rather than bouncing to the map with nothing said.
   */
  {
    path: 'knowledge-base',
    loadChildren: () => import('./knowledge-base/knowledge-base.module').then(m => m.KnowledgeBaseModule),
    canActivate: [AuthGuard],
    data: { shouldHaveRoles: ['KnowledgeBaseExternal', 'KnowledgeBaseInternal'] },
  },
  {
    path: 'soil-preparation/:recId',
    loadChildren: () => import('./soil-preparation/soil-preparation.module').then(m => m.SoilPreparationModule),
    canActivate: [AuthGuard],
    data: { shouldHaveRoles: ["RecommendationManager"] },
  },
  {
    path: "soil-maintenance/:recId",
    loadChildren: () => import("./soil-preparation/soil-preparation.module").then(m => m.SoilPreparationModule),
  
    data: { shouldHaveRoles: ['RecommendationManager'] }
  },
  {
    path: 'soil-crop-nutrition/:recId',
    loadChildren: () => import('./soil-crop-nutrition/soil-crop-nutrition-view.module').then(m => m.SoilCropNutritionViewModule),
    canActivate: [AuthGuard],
    data: { shouldHaveRoles: ['RecommendationManager'] }
  },
  {
    // Single View of Customer (YA-404). AllFarm only in practice: the three
    // roles exist only in the AllFarm database, so Precision Gateway and
    // Southern Gateway users never match and never see the route or nav entry.
    path: 'single-view',
    loadChildren: () => import('./single-view/single-view.module').then(m => m.SingleViewModule),
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['SingleViewCustomer', 'SingleViewAssignFinance', 'SingleViewLifetimeValue']
    }
  },
  {
    // Order to Cash revamp. Runs alongside the legacy order-manager route above and does
    // not replace it. OTCRevamp decides who gets in; the legacy OTC roles still decide
    // what they can do once inside.
    path: 'order-manager-v2',
    loadChildren: () => import('./order-manager-revamp/order-manager-revamp.module').then(m => m.OrderManagerRevampModule),
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['OTCRevamp']
    }
  },
  {
    path: 'maps',
    loadChildren: () => import('./maps/maps.module').then(m => m.MapsModule),
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['AngularMaps']
    }
  },
  {
    path: 'field-inspection',
    loadChildren: () => import('./field-inspection/field-inspection.module').then(m => m.FieldInspectionModule),
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: ['InspectionRequestor', 'Inspector', 'InspectionViewer']
    }
  },
  // GIS management (YA-164). Must stay ABOVE the '**' catch-all below or it is unreachable.
  //
  // THIS GUARD IS A UX CONVENIENCE, NOT THE SECURITY BOUNDARY. It hides the nav entry and
  // redirects anyone who types the URL. The real enforcement is server-side: the Lab Manager API
  // resolves the caller from the JWT and checks the GISPersonnel role on EVERY request to /gis/**,
  // independently of anything the client does. Do not remove or weaken that check on the basis
  // that this guard exists - a guard that runs in the browser protects nobody who does not want
  // to be protected.
  //
  // shouldHaveRoles comes from GIS_ROUTE_ROLES rather than a literal, and that file explains why
  // 'AngularField', 'AngularProfile' and 'AngularPinManager' must never appear in it:
  // auth.guard.ts:96-100 short-circuits to hasAccess = true for any of those three with no role
  // lookup, which would open this route to every logged-in user.
  //
  // canActivate is at route level, NOT inside data. Several order-manager routes in this file
  // nest it inside data, where it does nothing at all.
  {
    path: 'gis',
    loadChildren: () => import('./gis-management/gis-management.module').then(m => m.GisManagementModule),
    canActivate: [AuthGuard],
    data: {
      shouldHaveRoles: GIS_ROUTE_ROLES
    }
  },
  {
    path: '**',
    redirectTo: ''
  }
  // {
  //   path: '**',
  //   component: ErrorPage
  // },
];

@NgModule({
  imports: [RouterModule.forRoot(routes,  {onSameUrlNavigation: 'reload'})],
  exports: [RouterModule]
})
export class AppRoutingModule { }
